/**
 * Store des préférences utilisateur — Phase 1, incrément 5.
 *
 * Pour l'instant un seul réglage : `notifs` (toggle on/off des
 * notifications push). Le proto stockait ça dans `CFG.notifs`. Persisté
 * dans `localStorage.nestor_prefs`.
 *
 * En Phase 7, ce flag pilotera réellement l'inscription web-push / FCM /
 * APNS via le service worker. En Phase 1 il n'a aucun effet runtime —
 * c'est juste une intention.
 */
import { createStore } from '../lib/store.js';

export const STORAGE_KEY_PREFS = 'nestor_prefs';

export interface Preferences {
  /** Notifications push activées (intention — Phase 7 câble le SW). */
  notifs: boolean;
}

const DEFAULT_PREFS: Preferences = { notifs: true };

function safeLocalStorage(): Storage | undefined {
  try {
    return typeof globalThis !== 'undefined' && 'localStorage' in globalThis
      ? globalThis.localStorage
      : undefined;
  } catch {
    return undefined;
  }
}

function loadPrefs(storage: Storage | undefined): Preferences {
  if (!storage) return { ...DEFAULT_PREFS };
  try {
    const raw = storage.getItem(STORAGE_KEY_PREFS);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return { notifs: parsed.notifs ?? DEFAULT_PREFS.notifs };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function persistPrefs(storage: Storage | undefined, prefs: Preferences): void {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY_PREFS, JSON.stringify(prefs));
  } catch {
    // Quota / indisponible : on continue sans persister.
  }
}

const prefsStore = createStore<Preferences>(loadPrefs(safeLocalStorage()));

prefsStore.subscribe((state) => {
  persistPrefs(safeLocalStorage(), state);
});

export function getPreferences(): Preferences {
  return prefsStore.getState();
}

export function setNotifs(enabled: boolean): void {
  if (prefsStore.getState().notifs === enabled) return;
  prefsStore.setState({ notifs: enabled });
}

export function subscribePreferences(listener: (state: Preferences) => void): () => void {
  return prefsStore.subscribe(listener);
}

/** @internal pour tests. */
export function _resetPreferencesForTests(prefs: Partial<Preferences> = {}): void {
  prefsStore.setState({ ...DEFAULT_PREFS, ...prefs }, true);
}

/** @internal pour tests. */
export function _clearPreferencesStorageForTests(): void {
  const storage = safeLocalStorage();
  if (storage) storage.removeItem(STORAGE_KEY_PREFS);
}
